'use client';

import { c as _c } from "react/compiler-runtime";
import { type FactiiiTag } from '@shared/all/domains/factiiis';
import toast from '@shared/utils/helpers/toast';
import { useFactiiiHandlers } from '@shared/utils/hooks/useFactiiiHandlers';
import { CheckCircle2, LocateFixed, Trash } from 'lucide-react';
import { useState } from 'react';
import Button from '../../common/Button';
import Input from '../../UI/Input';
type Props = {
  locationSaveHandler: (value: string) => void;
  triggerRemove: () => void;
  tag: FactiiiTag;
  disabled?: boolean;
};
const DefaultLocationFactiii = t0 => {
  const $ = _c(44);
  const {
    locationSaveHandler,
    triggerRemove,
    tag,
    disabled
  } = t0;
  const [locationValue, setLocationValue] = useState(tag.postFactiiis?.[0]?.spaceTimes?.[0]?.locationValue || "");
  const {
    validateLocation
  } = useFactiiiHandlers();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = value => {
      setLocationValue(value);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleLocationChange = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(position => {
          const {
            latitude,
            longitude
          } = position.coords;
          const coords = `${latitude},${longitude}`;
          setLocationValue(coords);
        }, _temp);
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleHereClick = t2;
  let t3;
  if ($[2] !== locationSaveHandler || $[3] !== locationValue) {
    t3 = () => {
      locationSaveHandler(locationValue);
    };
    $[2] = locationSaveHandler;
    $[3] = locationValue;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleSave = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setLocationValue("");
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleClear = t4;
  let t5;
  if ($[6] !== locationValue || $[7] !== validateLocation) {
    t5 = validateLocation(locationValue);
    $[6] = locationValue;
    $[7] = validateLocation;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const locationError = !t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <LocateFixed className="size-4" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== disabled) {
    t7 = [<Button key="here" type="button" variant="TRANSPARENT" classNames="!p-1" onClick={handleHereClick} aria-label="Get current location" titleElement={t6} tooltip="Use current location" disabled={disabled} />];
    $[10] = disabled;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const inputIcons = t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span>Coordinates {"<lat.xxx>,<lng.xxx>"}</span>;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== locationError || $[14] !== locationValue) {
    t9 = !locationError && locationValue && <CheckCircle2 className="size-4 text-green-500" />;
    $[13] = locationError;
    $[14] = locationValue;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = <div className="flex items-center space-x-2"><label className="flex items-center space-x-2">{t8}{t9}</label></div>;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = e => handleLocationChange(e);
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = Boolean(locationError);
  let t13;
  if ($[19] !== disabled || $[20] !== inputIcons || $[21] !== locationValue || $[22] !== t12) {
    t13 = <Input disabled={disabled} value={locationValue} onChange={t11} label="Coordinates (34.052235, -118.243683)" warning={t12} className="grow" icons={inputIcons} />;
    $[19] = disabled;
    $[20] = inputIcons;
    $[21] = locationValue;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== locationError) {
    t14 = locationError && <p className="text-sm text-destructive">Please enter valid coordinates in the format: latitude, longitude</p>;
    $[24] = locationError;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const t15 = disabled || locationError;
  let t16;
  if ($[26] !== handleSave || $[27] !== t15) {
    t16 = <Button type="button" classNames="w-full" borderTransparent={false} text="Save" onClick={handleSave} disabled={t15} />;
    $[26] = handleSave;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== disabled) {
    t17 = <Button variant="SECONDARY" type="button" classNames="w-full" borderTransparent={false} text="Clear" onClick={handleClear} disabled={disabled} />;
    $[29] = disabled;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== tag.isNewFactiii || $[32] !== tag.isRemovedFactiii || $[33] !== triggerRemove) {
    t18 = !tag.isNewFactiii && <Button variant="CLEAR" classNames={`p-1.5 ml-auto ${tag.isRemovedFactiii ? "border-destructive" : ""}`} borderTransparent={false} icon={<Trash width={20} height={20} color={tag.isRemovedFactiii ? "red" : "rgba(var(--text-secondary-color))"} />} tooltip={tag.isRemovedFactiii ? "Marked for removal" : "remove"} onClick={e_0 => {
      e_0.stopPropagation();
      triggerRemove();
    }} />;
    $[31] = tag.isNewFactiii;
    $[32] = tag.isRemovedFactiii;
    $[33] = triggerRemove;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t16 || $[36] !== t17 || $[37] !== t18) {
    t19 = <div className="flex items-center space-x-2">{t16}{t17}{t18}</div>;
    $[35] = t16;
    $[36] = t17;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t10 || $[40] !== t13 || $[41] !== t14 || $[42] !== t19) {
    t20 = <div className="space-y-2">{t10}{t13}{t14}{t19}</div>;
    $[39] = t10;
    $[40] = t13;
    $[41] = t14;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  return t20;
};
export default DefaultLocationFactiii;
function _temp(error) {
  console.error("Error getting location:", error);
  toast(`Unable to retrieve your location, ${error.message}`, "", "error");
}